import React, { useContext, useEffect } from "react"

import { isValidNumber } from "libphonenumber-js"
import { useDispatch, useSelector } from "react-redux"
import { v4 as uuidv4 } from "uuid"

import { batchVerifyAndGetUserInfo } from "@actions/batchVerifyAndGetUserInfo"
import { setCreateTeeupScreenType } from "@actions/createTeeupActions"
import { removeSelectedContact } from "@actions/inviteesActions"
import { ButtonEl } from "@components/common"
import { BUTTON_VARIANTS } from "@constants/button"
import { CREATE_TEEUP_SCREENS } from "@constants/teeups"
import { InvitePeopleContext } from "@contexts/InvitePeopleContext"
import { selectSyncContactsConnectors } from "@selectors/contacts"
import { selectSelectedContacts } from "@selectors/invitees"
import {
  contactTypes,
  formatContact,
  getEmailLabel,
  getPhoneLabel,
  isCooeUser,
  userTypes,
} from "@utils/contactUtils"
import { fetchSyncContactsData } from "@utils/contacts"
import { getIsEmail } from "@utils/getIsEmail"
import { isContainsOnlyNumbers } from "@utils/isContainsOnlyNumbers"
import { InviteSearch } from "components"

import { useCreateTeeUp } from "../../pages/CreateTeeUpNd/CreateTeeUpContext/useCreateTeeUp"
import { ContactsList } from "../ContactsList"

import {
  ManualInviteNotice,
  SubmitWrapper,
  Wrapper,
} from "./createTeeupInviteStyles"

export const CreateTeeupInviteScreen = ({
  setInvitees: setInviteesProp,
  setInviteToTeeUpById,
}) => {
  const dispatch = useDispatch()
  const { setInvitees } = useCreateTeeUp()
  const selectedContacts = useSelector(selectSelectedContacts)
  const selectedContactsLength = selectedContacts.length
  const contactsConnectors = useSelector(selectSyncContactsConnectors)
  const { setNotFoundEmails } = useContext(InvitePeopleContext)
  // WL4-1: a manually-typed (not-yet-a-CooWe-user) chip has no account to add to the TeeUp
  // directly -- the backend records a pending_invites row and emails them a join link
  // (comms.notify.notify_invite_channel). Set the right expectation before Send instead of
  // silently no-oping from the user's point of view. NOTE: chips from the typed-email/phone
  // "Invite by ..." row (InviteNewContact) are shaped {id, matchingContact} with NO userType,
  // while prepareManualContact chips carry userType MANUAL -- so the check is "anything that
  // is not an already-resolved CooWe user", which covers both shapes.
  const hasManualInvitee = selectedContacts.some(contact => !isCooeUser(contact))

  useEffect(() => {
    fetchSyncContactsData(contactsConnectors)
  }, [contactsConnectors])

  const formatRest = data => {
    return data.map(contact => {
      const id = uuidv4()
      const userObj = {
        id,
        value: contact,
        lastName: "",
        userType: userTypes.MANUAL,
      }
      if (isContainsOnlyNumbers(contact)) {
        userObj.firstName = getPhoneLabel(contact)
        userObj.contactType = contactTypes.PHONE
      } else {
        userObj.firstName = getEmailLabel(contact)
        userObj.contactType = contactTypes.EMAIL
      }

      return userObj
    })
  }

  const handleSendInvitations = async () => {
    // BUG-RCNEXT-B1-INVITE-WEB root cause: a picker-resolved CooWe app user (directory/frequent
    // match, isCooeUser(contact) true) already carries a real id — but the dev directory doesn't
    // expose emails/phones for other tenant users (see useFilteredContacts), so `value` fell back
    // to `contact.username` (e.g. "maya_chen"), which is neither a valid email nor phone number.
    // That failed the validation below UNCONDITIONALLY for every such contact, so Send aborted
    // with "'undefined' couldn't be added" before batchVerifyAndGetUserInfo (or any network call)
    // ever ran. Fix: an already-resolved CooWe user needs no re-verification at all — use it
    // directly. Only contacts that are NOT already a resolved user go through the
    // validate -> batchVerifyAndGetUserInfo round trip (manual typed emails/phones, or synced
    // phone-book contacts).
    const resolvedUsers = []
    const values = []
    const notValidContacts = []
    const notValidContactIds = []

    selectedContacts.forEach(contact => {
      if (isCooeUser(contact)) {
        resolvedUsers.push(contact)
        return
      }

      // WL4-1 (fix-forward, same class as BUG-RCNEXT-B1-INVITE-WEB above): a MANUALLY-TYPED
      // chip (contactUtils.prepareManualContact -> userType: MANUAL) carries its typed
      // email/phone in `contact.value`, not `matchingContact`/`emails`/`phoneNumbers` (those
      // belong to a directory/phone-book contact shape). Without this fallback, `value`
      // resolved to "" for every manual chip, which then failed the email/phone validity
      // check below UNCONDITIONALLY -> Send aborted via the notValidContacts branch before
      // batchVerifyAndGetUserInfo (or any network call) ever ran -- the "Send fires zero
      // requests" defect for typed invitees.
      const value =
        contact.matchingContact ||
        contact.value ||
        contact.emails?.[0]?.email ||
        contact.phoneNumbers?.[0]?.number ||
        ""
      values.push(value)

      if (
        !isContainsOnlyNumbers(value) &&
        !getIsEmail(value) &&
        !isValidNumber(value)
      ) {
        notValidContacts.push(contact)
        notValidContactIds.push(contact.id)
      }
    })

    if (notValidContacts.length > 0) {
      // Same manual-chip fallback as the value resolution above -- otherwise a genuinely
      // invalid manual entry renders as "undefined" in the not-found-emails UI instead of the
      // value the user actually typed.
      setNotFoundEmails(
        notValidContacts.map(
          ({ matchingContact, value }) => matchingContact || value
        )
      )
      dispatch(removeSelectedContact(notValidContactIds))
      return
    }

    // BUG-RCNEXT-B1-INVITE-WEB: batchVerifyAndGetUserInfo is a RAW fetch to /users/search that
    // bypasses the axios-based dev shim; on dev it now self-repoints to the real directory (see
    // actions/batchVerifyAndGetUserInfo.js). Guard it regardless so a network hiccup still falls
    // back to treating the selected values as manual invitees instead of dropping them.
    let users = []
    let contacts = []
    let rest = []
    if (values.length > 0) {
      try {
        const res = await batchVerifyAndGetUserInfo(values)
        users = res?.users || []
        contacts = res?.contacts || []
        rest = res?.rest || []
      } catch (e) {
        rest = values
      }
    }
    const invitees = [
      ...resolvedUsers,
      ...users,
      ...contacts.map(contact => formatContact(contact)),
      ...formatRest(rest),
    ]

    if (setInviteesProp) {
      setInviteesProp(invitees)
      setInviteToTeeUpById(true)
      dispatch(removeSelectedContact(selectedContacts.map(c => c.id)))
      setNotFoundEmails([])
      return
    }

    setInvitees(invitees)
    dispatch(setCreateTeeupScreenType(CREATE_TEEUP_SCREENS.MAIN))
    setNotFoundEmails([])
  }

  return (
    <>
      <Wrapper $isSelectedContacts={selectedContactsLength > 0}>
        <InviteSearch />

        <ContactsList />
      </Wrapper>

      {selectedContactsLength > 0 && (
        <SubmitWrapper>
          {hasManualInvitee && (
            <ManualInviteNotice data-testid="manual-invite-email-notice">
              An invitation email will be sent to anyone not yet on CooWe.
            </ManualInviteNotice>
          )}
          <ButtonEl
            isBlack
            fullWidth
            testID="send-invitations-button"
            variant={BUTTON_VARIANTS.ROUNDED}
            text={`Send ${selectedContactsLength} invitations`}
            onClick={handleSendInvitations}
          />
        </SubmitWrapper>
      )}
    </>
  )
}
